/**
 * OverbookCapacityStrip
 * ──────────────────────────────────────────────────────────────────────
 * Reads `GET /api/scheduler/slots/capacity` for each visible provider on
 * the selected date and renders a compact capacity tile per provider:
 *
 *   [Provider] 8/8 booked · 2 overbook (2 left)
 *
 * Behaviour:
 *   • Re-fetches when `date`, `entityIds`, or `refreshKey` changes.
 *   • Soft-fails per provider — a single 500 doesn't blank the strip.
 *   • Hides itself entirely when there are zero providers OR when the
 *     policy default-safe and no overbooking activity is observed
 *     (avoids visual clutter for the 99% normal-day case).
 *   • Exposes `onOverbookClick(entityId)` so the parent can open the
 *     OverbookDialog with the right prefill.
 *
 * Pure read — never mutates anything.
 */
import { useEffect, useState, useCallback } from 'react';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { ShieldAlert } from 'lucide-react';
import * as schedulerApi from '../../../../lib/api/schedulerApi';

const OverbookCapacityStrip = ({
  date,
  entityIds = [],
  locationId = null,
  onOverbookClick,
  refreshKey = 0,
  allowOverbook = true,
}) => {
  const [rows, setRows] = useState([]);     // [{entity_id, capacity, booked, ...}]
  const [loading, setLoading] = useState(false);

  const fetchCapacity = useCallback(async () => {
    if (!date || entityIds.length === 0) {
      setRows([]);
      return;
    }
    setLoading(true);
    const results = await Promise.all(entityIds.map(async (eid) => {
      const res = await schedulerApi.getSlotCapacity({
        entity_id: eid,
        date,
        ...(locationId ? { location_id: locationId } : {}),
      });
      if (res.ok) return { entity_id: eid, ...res.data };
      return null;
    }));
    setRows(results.filter(Boolean));
    setLoading(false);
  }, [date, entityIds.join(','), locationId, refreshKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchCapacity(); }, [fetchCapacity]);

  if (entityIds.length === 0) return null;

  // Hide the strip when nothing is interesting to show: every provider has
  // headroom AND the policy is default-safe AND no overbooked appts exist.
  const interesting = rows.some((r) => (
    r.booked >= r.capacity
    || (r.overbooked || 0) > 0
    || (r.overbook_cap || 0) > 0
  ));
  if (!loading && !interesting) return null;

  return (
    <div
      className="flex flex-wrap gap-2 p-2 rounded-md border border-slate-200 bg-slate-50/70"
      data-testid="overbook-capacity-strip"
    >
      {rows.map((r) => {
        const atCap     = r.booked >= r.capacity;
        const overUsed  = (r.overbooked || 0) > 0;
        const canOverbk = atCap && allowOverbook;
        return (
          <div
            key={r.entity_id}
            className="flex items-center gap-1.5 text-[11px]"
            data-testid={`overbook-capacity-row-${r.entity_id}`}
          >
            <Badge
              variant="outline"
              className={
                atCap
                  ? 'border-amber-300 text-amber-700 bg-amber-50'
                  : 'border-slate-200 text-slate-600 bg-white'
              }
            >
              {r.booked}/{r.capacity}
            </Badge>
            {overUsed && (
              <Badge
                variant="outline"
                className="border-red-200 text-red-700 bg-red-50"
                data-testid={`overbook-capacity-overbooked-${r.entity_id}`}
              >
                +{r.overbooked} overbooked
              </Badge>
            )}
            {(r.overbook_cap || 0) > 0 && (
              <span className="text-[10px] text-slate-500">
                ({r.overbook_headroom} left)
              </span>
            )}
            {canOverbk && (
              <Button
                size="sm"
                variant="outline"
                className="h-6 px-2 text-[10px] border-amber-300 text-amber-700 hover:bg-amber-50"
                onClick={() => onOverbookClick && onOverbookClick(r.entity_id)}
                data-testid={`overbook-capacity-action-${r.entity_id}`}
              >
                <ShieldAlert className="h-3 w-3 mr-1" />
                Overbook
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default OverbookCapacityStrip;
